import React, { useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { RESERVED_ORG_CODES } from '../App';
import Lottie from 'lottie-react';
import registerAnimation from '../assets/animations/register-animation.json';
import apiClient from '../services/apiClient';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// Step constants
const STEP_EMAIL = 'email';   // Enter email, request OTP
const STEP_OTP = 'otp';     // Enter 6-digit OTP
const STEP_DETAILS = 'details'; // Fill remaining registration fields

function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgCode: rawOrgCode } = useParams();
  // A bare `/register` also matches the app's `/:orgCode/*` route, with
  // "register" itself captured as orgCode — not a real org, so ignore it.
  const orgCode = rawOrgCode && !RESERVED_ORG_CODES.includes(rawOrgCode.toLowerCase()) ? rawOrgCode : undefined;
  const orgPrefix = orgCode ? `/${orgCode}` : '';
  const { register } = useAuth();

  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [emailVerifiedToken, setEmailVerifiedToken] = useState('');
  const [cooldown, setCooldown] = useState(0); // seconds until resend allowed

  const [formData, setFormData] = useState({
    username: '', password: '', password2: '', firstName: '', lastName: '', cnic: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const inputClass =
    'w-full px-4 py-3 border border-slate-200 rounded-xl text-[15px] bg-slate-50 ' +
    'transition-all duration-200 focus:outline-none focus:border-slate-900 focus:bg-white ' +
    'focus:ring-4 focus:ring-slate-900/10 hover:border-slate-300 ' +
    'disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed placeholder:text-slate-400';

  // ── Step 1: send OTP ───────────────────────────────────────────
  const startCooldown = () => {
    setCooldown(60);
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address'); return; }
    setError('');
    setLoading(true);
    try {
      await apiClient.post('/auth/send_email_otp/', { email: email.trim().toLowerCase() });
      setStep(STEP_OTP);
      startCooldown();
    } catch (err) {
      setError(err.response?.data?.error || t('register.sendFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (cooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      await apiClient.post('/auth/send_email_otp/', { email: email.trim().toLowerCase() });
      setOtp('');
      startCooldown();
    } catch (err) {
      setError(err.response?.data?.error || t('register.resendFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: verify OTP ─────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError('Please enter the 6-digit code'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await apiClient.post('/auth/verify_email_otp/', {
        email: email.trim().toLowerCase(),
        code: otp.trim(),
      });
      setEmailVerifiedToken(res.data.email_verified_token);
      setStep(STEP_DETAILS);
    } catch (err) {
      setError(err.response?.data?.error || t('register.invalidExpired'));
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: complete registration ──────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username || !formData.password || !formData.password2) {
      setError(t('register.validationFillFields'));
      return;
    }
    if (formData.password.length < 8) {
      setError(t('register.validationPasswordLength'));
      return;
    }
    if (formData.password !== formData.password2) {
      setError(t('register.validationPasswordMatch'));
      return;
    }
    if (formData.cnic && !/^\d{5}-\d{7}-\d{1}$/.test(formData.cnic)) {
      setError(t('register.validationCnicFormat'));
      return;
    }

    setLoading(true);
    const result = await register(
      formData.username,
      email,
      formData.password,
      formData.password2,
      formData.firstName,
      formData.lastName,
      emailVerifiedToken,
      formData.cnic,
    );
    setLoading(false);

    if (result.success) {
      navigate(`${orgPrefix}/`);
    } else {
      const errorMessage = typeof result.error === 'object'
        ? (result.error?.non_field_errors?.join(', ') || JSON.stringify(result.error))
        : (result.error || t('register.registrationFailed'));
      setError(errorMessage);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  const stepLabels = { [STEP_EMAIL]: 1, [STEP_OTP]: 2, [STEP_DETAILS]: 3 };
  const stepTitles = {
    [STEP_EMAIL]: t('register.stepEmailTitle'),
    [STEP_OTP]: t('register.stepOtpTitle'),
    [STEP_DETAILS]: t('register.stepDetailsTitle'),
  };
  const stepSubtitles = {
    [STEP_EMAIL]: t('register.stepEmailSubtitle'),
    [STEP_OTP]: t('register.stepOtpSubtitle', { email }),
    [STEP_DETAILS]: t('register.stepDetailsSubtitle'),
  };

  return (
    <div className="fixed inset-0 overflow-y-auto bg-gradient-to-br from-[#0f172a] to-[#1e293b] z-10">
      <div className="flex min-h-full items-center justify-center p-5">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md px-10 py-12 border border-white/10 animate-slide-up">

          {/* Lottie */}
          <div className="flex justify-center mb-6 animate-fade-in">
            <Lottie animationData={registerAnimation} loop={true} style={{ width: 160, height: 160 }} />
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3].map(n => (
              <React.Fragment key={n}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                  ${stepLabels[step] >= n ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'}`}>
                  {n}
                </div>
                {n < 3 && <div className={`h-0.5 w-8 ${stepLabels[step] > n ? 'bg-slate-900' : 'bg-slate-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-[1.75rem] font-bold tracking-tight text-slate-900 mb-1">{stepTitles[step]}</h2>
            <p className="text-sm font-medium text-slate-500">{stepSubtitles[step]}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
              {error}
            </div>
          )}

          {/* ── Step 1: Email ── */}
          {step === STEP_EMAIL && (
            <form onSubmit={handleSendOtp} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-sm font-semibold text-slate-800">
                  {t('register.emailAddress')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email" id="email"
                  value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder={t('register.emailPlaceholder')} required disabled={loading}
                  className={inputClass}
                />
              </div>
              <button
                type="submit" disabled={loading}
                className="mt-1 w-full py-3 px-5 text-base font-bold text-white rounded-xl
                bg-gradient-to-br from-slate-900 to-slate-700 shadow-md
                transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl
                active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('register.sendingCode') : t('register.sendVerificationCode')}
              </button>
            </form>
          )}

          {step === STEP_OTP && (
            <form onSubmit={handleVerifyOtp} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="otp" className="text-sm font-semibold text-slate-800">
                  {t('register.sixDigitCode')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text" id="otp" inputMode="numeric" maxLength={6}
                  value={otp} onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                  placeholder="123456" required disabled={loading}
                  className={`${inputClass} text-center text-2xl tracking-[0.5em]`}
                />
              </div>
              <button
                type="submit" disabled={loading}
                className="mt-1 w-full py-3 px-5 text-base font-bold text-white rounded-xl
                bg-gradient-to-br from-slate-900 to-slate-700 shadow-md
                transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl
                active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('register.verifying') : t('register.verifyCode')}
              </button>
              <div className="text-center text-sm text-slate-500">
                {t('register.didNotReceive')}{' '}
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={cooldown > 0 || loading}
                  className="font-bold text-slate-900 disabled:text-slate-400 hover:underline"
                >
                  {cooldown > 0 ? t('register.resendIn', { seconds: cooldown }) : t('register.resend')}
                </button>
                {' · '}
                <button
                  type="button"
                  onClick={() => { setStep(STEP_EMAIL); setOtp(''); setError(''); }}
                  className="font-bold text-slate-900 hover:underline"
                >
                  {t('register.changeEmail')}
                </button>
              </div>
            </form>
          )}

          {/* ── Step 3: Details ── */}
          {step === STEP_DETAILS && (
            <form onSubmit={handleRegister} className="flex flex-col gap-5">
              {/* Email (read-only, already verified) */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-800">{t('register.email')}</label>
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm font-medium">
                  <span>✓</span>
                  <span>{email}</span>
                </div>
              </div>

              {/* Name row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label htmlFor="firstName" className="text-sm font-semibold text-slate-800">{t('register.firstName')}</label>
                  <input
                    type="text" id="firstName" name="firstName"
                    value={formData.firstName} onChange={handleChange}
                    placeholder={t('register.firstNamePlaceholder')} disabled={loading} className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="lastName" className="text-sm font-semibold text-slate-800">{t('register.lastName')}</label>
                  <input
                    type="text" id="lastName" name="lastName"
                    value={formData.lastName} onChange={handleChange}
                    placeholder={t('register.lastNamePlaceholder')} disabled={loading} className={inputClass}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="username" className="text-sm font-semibold text-slate-800">
                  {t('register.username')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text" id="username" name="username"
                  value={formData.username} onChange={handleChange}
                  placeholder={t('register.usernamePlaceholder')} required disabled={loading} className={inputClass}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="cnic" className="text-sm font-semibold text-slate-800">{t('register.cnic')}</label>
                <input
                  type="text" id="cnic" name="cnic"
                  value={formData.cnic} onChange={handleChange}
                  placeholder={t('register.cnicPlaceholder')} disabled={loading}
                  className={inputClass} maxLength={15}
                />
                <p className="text-xs text-slate-400">{t('register.cnicFormat')}</p>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="password" className="text-sm font-semibold text-slate-800">
                  {t('register.password')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'} id="password" name="password"
                    value={formData.password} onChange={handleChange}
                    placeholder={t('register.passwordPlaceholder')} required disabled={loading} className={inputClass + ' pr-11'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    disabled={loading}
                    tabIndex={-1}
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center bg-transparent border-0 p-0 leading-none text-slate-400 hover:text-slate-600 disabled:opacity-50 focus:outline-none"
                  >
                    {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="password2" className="text-sm font-semibold text-slate-800">
                  {t('register.confirmPassword')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword2 ? 'text' : 'password'} id="password2" name="password2"
                    value={formData.password2} onChange={handleChange}
                    placeholder={t('register.confirmPasswordPlaceholder')} required disabled={loading} className={inputClass + ' pr-11'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword2(prev => !prev)}
                    disabled={loading}
                    tabIndex={-1}
                    aria-label={showPassword2 ? t('login.hidePassword') : t('login.showPassword')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center bg-transparent border-0 p-0 leading-none text-slate-400 hover:text-slate-600 disabled:opacity-50 focus:outline-none"
                  >
                    {showPassword2 ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                  </button>
                </div>
              </div>

              <button
                type="submit" disabled={loading}
                className="mt-1 w-full py-3 px-5 text-base font-bold text-white rounded-xl
                bg-gradient-to-br from-slate-900 to-slate-700 shadow-md
                transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl
                active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('register.registeringAccount') : t('register.createAccount')}
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="text-center mt-6 pt-6 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              {t('register.alreadyHaveAccount')}{' '}
              <Link to={`${orgPrefix}/login`} className="text-slate-900 font-bold no-underline hover:underline">
                {t('register.login')}
              </Link>
            </p>
          </div>

          {/* Powered by */}
          <p className="text-center text-xs text-slate-400 mt-6">
            Powered by{' '}
            <span className="font-semibold text-slate-500">Elysian Systems</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;

